import {
  Box,
  Flex,
  Spinner,
  Text,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td
} from '@chakra-ui/react';
import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ButtonIcon, OutlineCard, Select } from '@morressier/alembic';
import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchJournalConfiguredChecklists,
  fetchSubmissionContext
} from 'src/api/queries';
import {
  IEditorialFormSchema,
  IWorkPhase
} from '@publishing-engine/domain/models';

import CheckListFormModal from './CheckListFormModal';

// ---- Types ----

interface CheckListsProps {
  journalId: string;
}

interface ModalState {
  isOpen: boolean;
  phase: IWorkPhase;
  phaseLabel: string;
}

// ---- Constants ----

// The three editorial phases that carry a checklist. Labels mirror the
// `editorialPhaseMap` entries used across the rest of the configuration UI.
const CHECKLIST_PHASES: { phase: IWorkPhase; label: string }[] = [
  { phase: 'initialAssessment', label: 'Initial Assessment' },
  { phase: 'preProduction', label: 'Pre Production' },
  { phase: 'revisionAssessment', label: 'Revision Assessment' },
  { phase: 'immediateRevision', label: 'Immediate Revision' },
  { phase: 'revision', label: 'Revision' }
];

// ---- Main component ----

const CheckLists: React.FC<CheckListsProps> = ({ journalId }) => {
  // ---- Modal state ----
  const [modal, setModal] = useState<ModalState | null>(null);

  // ---- Selected article workflow state ----
  const [selectedArticleWorkflowId, setSelectedArticleWorkflowId] =
    useState<string | null>(null);

  // ---- Fetch journal to resolve the list of article types ----
  const { data: journal, isLoading: isJournalLoading } = useQuery({
    queryKey: ['submission-context', journalId],
    queryFn: () => (journalId ? fetchSubmissionContext(journalId) : null),
    enabled: !!journalId
  });

  const articleTypes = journal?.articleTypes ?? [];

  // Keep the selection valid: select the first article type on initial load,
  // and fall back to another valid article type if the currently selected one
  // is removed or no longer exists.
  useEffect(() => {
    const firstArticleType = articleTypes[0];

    if (!firstArticleType) {
      setSelectedArticleWorkflowId(null);
      return;
    }

    const selectionStillValid = articleTypes.some(
      articleType => articleType.articleWorkflowId === selectedArticleWorkflowId
    );

    if (selectionStillValid) return;

    setSelectedArticleWorkflowId(firstArticleType.articleWorkflowId);
  }, [articleTypes, selectedArticleWorkflowId]);

  // ---- Fetch configured (saved) checklists for the selected article workflow ----
  const { data: configuredData, isLoading: isConfiguredLoading } = useQuery({
    queryKey: [
      'journal-configuration-checklists',
      journalId,
      selectedArticleWorkflowId
    ],
    queryFn: () =>
      fetchJournalConfiguredChecklists(
        journalId,
        selectedArticleWorkflowId as string
      ),
    enabled: !!selectedArticleWorkflowId
  });

  // Map phase -> configured checklist schema
  const configuredByPhase = useMemo(() => {
    const result: Partial<Record<IWorkPhase, IEditorialFormSchema>> = {};
    for (const schema of configuredData?.editorialFormSchemas ?? []) {
      result[schema.phase as IWorkPhase] = schema;
    }
    return result;
  }, [configuredData]);

  // ---- Handlers ----

  const openModal = useCallback((phase: IWorkPhase, phaseLabel: string) => {
    setModal({ isOpen: true, phase, phaseLabel });
  }, []);

  const closeModal = useCallback(() => {
    setModal(null);
  }, []);

  const handleArticleTypeChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      setSelectedArticleWorkflowId(event.target.value);
      // The modal is tied to the previously selected article type, so close it
      // to avoid mixing checklist data across article types.
      setModal(null);
    },
    []
  );

  // ---- Render ----

  return (
    <Flex flexDirection={'column'} gap={8}>
      <OutlineCard borderless paddingX={4} paddingY={6}>
        <Flex flexDirection={'column'} padding={2} gap={8}>
          <Flex flexDirection="column" gap={2}>
            <Text textStyle={'headingSection'}>Checklists</Text>
            <Text textStyle="textBody">
              Configure checklist items for each article type and phase.
            </Text>
          </Flex>

          {/* Journal loading state */}
          {isJournalLoading ? (
            <Flex justifyContent="center" alignItems="center" paddingY={12}>
              <Spinner />
            </Flex>
          ) : articleTypes.length === 0 ? (
            // No article types state
            <OutlineCard borderless paddingX={4} paddingY={6}>
              <Flex
                flexDirection={'column'}
                gap={2}
                alignItems="center"
                paddingY={8}
              >
                <Text textStyle="headingGroup">No article types available</Text>
                <Text textStyle="textBody">
                  Add an article type to this journal before configuring
                  checklists.
                </Text>
              </Flex>
            </OutlineCard>
          ) : (
            <>
              {/* Article type selector */}
              <Flex flexDirection="column" gap={2} maxWidth="320px">
                <Text textStyle="headingGroup">Article Type</Text>
                <Select
                  value={selectedArticleWorkflowId ?? ''}
                  onChange={handleArticleTypeChange}
                  aria-label="Article Type"
                >
                  {articleTypes.map(articleType => (
                    <option
                      key={articleType.articleWorkflowId}
                      value={articleType.articleWorkflowId}
                    >
                      {articleType.name}
                    </option>
                  ))}
                </Select>
              </Flex>

              {/* Phase / checklist table */}
              <Box overflowX="auto" width="100%">
                <Table>
                  <Thead>
                    <Tr
                      data-testId="checklists-table-header"
                      sx={{
                        '& th': {
                          textTransform: 'none',
                          color: 'black',
                          background: 'white'
                        }
                      }}
                    >
                      <Th p={4}>
                        <Text textStyle="headingItem" whiteSpace="nowrap">
                          Phase
                        </Text>
                      </Th>
                      <Th p={4}>
                        <Text textStyle="headingItem" whiteSpace="nowrap">
                          Checklist Items
                        </Text>
                      </Th>
                      <Th width="100px" textAlign="center">
                        <Text textStyle="headingItem" whiteSpace="nowrap">
                          Actions
                        </Text>
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {CHECKLIST_PHASES.map(({ phase, label }) => {
                      const configured = configuredByPhase[phase];
                      const items = configured?.formFields ?? [];

                      return (
                        <Tr
                          key={phase}
                          data-testId="checklists-table-row"
                          background="white"
                          alignItems="center"
                          _last={{ borderBottom: 'none' }}
                        >
                          <Td>
                            <Text textStyle="headingItem">{label}</Text>
                          </Td>
                          <Td>
                            {isConfiguredLoading ? (
                              <Text textStyle="textContext" color="textSubtle">
                                Loading...
                              </Text>
                            ) : items.length === 0 ? (
                              <Text textStyle="textContext" color="textSubtle">
                                No checklist items configured
                              </Text>
                            ) : (
                              <Flex flexDirection="column" gap={1}>
                                <Text textStyle="textContext">
                                  {items.length === 1
                                    ? '1 checklist item'
                                    : `${items.length} checklist items`}
                                </Text>
                                <Text
                                  textStyle="textContext"
                                  color="textSubtle"
                                  isTruncated
                                  maxWidth="480px"
                                >
                                  {items
                                    .map(field => field.params.labels.main)
                                    .join(', ')}
                                </Text>
                              </Flex>
                            )}
                          </Td>
                          <Td>
                            <ButtonIcon
                              variant="secondary"
                              size="small"
                              ariaLabel={`Configure checklist for ${label}`}
                              icon={<FontAwesomeIcon icon={faEdit} />}
                              onClick={() => openModal(phase, label)}
                            />
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
            </>
          )}
        </Flex>
      </OutlineCard>

      {/* CheckListFormModal — self-contained: toggles items and saves internally */}
      {modal && selectedArticleWorkflowId && (
        <CheckListFormModal
          isOpen={modal.isOpen}
          journalId={journalId}
          articleWorkflowId={selectedArticleWorkflowId}
          phase={modal.phase}
          phaseLabel={modal.phaseLabel}
          onClose={closeModal}
        />
      )}
    </Flex>
  );
};

export default CheckLists;
